/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { isEmpty } from '@ember/utils';
import { SubjectStepExplanation, SubjectStepExplanationCause, SubjectStepExplanationCode } from 'cio/utils/subjects';

export const NOUN_PLACEHOLDER = '{{NOUN}}';

export const EXPLANATIONS: Record<SubjectStepExplanationCode, string | null> = {
  no_explanation: null,
  originally_scheduled: 'Originally scheduled',
  expired: null,
  window_satisfied: 'Time window conditions met',
  inactive: 'Leaving the campaign',
  send_disabled: 'Message sending is disabled',
  unsubscribed: 'Not sending, unsubscribed',
  previously_sent: 'Not sending, previously sent this message',
  preconditions: 'Action conditions not met',
  future_action: 'Waiting for campaign start',
  grace_period: 'Leaving the campaign after grace period',
  grace_expired: 'Grace period ended, leaving now',
  no_grace_for_events: 'Leaving the campaign (no grace period for events)',
  deleting: 'Action deleted',
  no_android_devices: "This {{NOUN}} didn't have an Android device",
  no_ios_devices: "This {{NOUN}} didn't have an iOS device",

  // Exit step explanations
  exit_finished: 'The {{NOUN}} moved through the entire campaign.',
  exit_manual: 'Reason: The {{NOUN}} was manually removed from the campaign.',
  exit_deleted_delay: "Reason: The {{NOUN}} was in a Wait Until delay that was deleted using the 'Exit' option.",
  exit_stopped: 'Reason: The campaign was stopped.',
  exit_archived: 'Reason: The campaign was archived.',
  exit_converted: 'Reason: The {{NOUN}} met the conversion goal.',
  exit_deleted: 'Reason: The campaign was deleted.',
  exit_criteria_not_matched: 'Reason: The {{NOUN}} stopped meeting the trigger and/or filter conditions.',
  exit_no_filter_match: "Reason: The {{NOUN}} met the trigger but didn't meet the filter conditions within 24 hours.",
  exit_no_match_after_grace_period:
    "Reason: The {{NOUN}} didn't meet the trigger and filter conditions when their grace period expired.",
  exit_conversion_without_delivery: 'Reason: The {{NOUN}} already met exit criteria before receiving any messages.',
  exit_conversion_without_converted_delivery: 'Reason: The {{NOUN}} met the exit criteria.',
};

const STEP_SPECIFIC_EXPLANATIONS: Record<string, string | null> = {
  skipped_delay_expired: 'Conditions met',
};

export const CAUSES: Record<SubjectStepExplanationCause, string | null> = {
  ingress_job: null,
  continue_job: null,
  retry_job: null,
  delete_job: null,
  shortened_delay: 'Shortened delay',
  rescheduled: 'Rescheduled',
  window_changed: 'Changed time window',
};

interface JourneyStepExplanationArgs {
  explanation: SubjectStepExplanation;
  noun?: string;
  stepType?: string;
  hideExplanation: boolean;
}

class JourneyStepExplanation extends Component<JourneyStepExplanationArgs> {
  get noun() {
    return this.args.noun ?? 'person';
  }

  get description(): string {
    let { cause, code } = this.args.explanation;
    let explanation = EXPLANATIONS[code];

    if (isEmpty(explanation) && !isEmpty(this.args.stepType)) {
      explanation = STEP_SPECIFIC_EXPLANATIONS[`${this.args.stepType}_${code}`];
    }

    let description = [CAUSES[cause], explanation].compact().join(' — ');

    return description.replace(NOUN_PLACEHOLDER, this.noun);
  }

  get hidden(): boolean {
    return this.args.hideExplanation || !this.description;
  }
}

export default JourneyStepExplanation;
